/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, OnChanges, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonStylesImage } from 'cms/cms-shared/common-settings-styles/image/common-settings-styles-image.models';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { AnalyticsComponentEvent } from 'idt-analytics-api/models';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { IdtImage } from 'shared/idt-components/idt-components-cms.model';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { toNumber } from 'shared/utils/data-utils';

import { PieceImageSettings } from './models/piece-image.models';


@Component({
  selector: 'piece-image-render',
  templateUrl: './piece-image-render.component.html',
  styleUrls: ['./piece-image-render.component.scss'],
})
export class PieceImageRenderComponent
  extends BaseRenderComponent<PieceImageSettings>
  implements OnChanges, OnInit {

  public orientation$!: Observable<string>;
  public analyticsEventClickImage?: string;
  public analyticsEventData: AnalyticsComponentEvent = {};

  constructor(
    @Inject('Window') private readonly window: Window,
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly productService: ProductsService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly elRef: ElementRef,
    private readonly renderer: Renderer2
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  public get hasToApplyAkamaiOptimization(): boolean {
    return this.settings.styles.objectFit === 'cover' && this.slot?.height === '100vh' && this.slot?.width === '100%';
  }

  public ngOnInit() {
    if (isDefined(this.settings.analytics)) {
      this.setAnalyticsEvents();
    }
  }

  public ngOnChanges() {
    const { objectFit, objectPosition } = this.settings.styles as CommonStylesImage;

    if (!!objectFit) {
      this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectFit', `${objectFit}`, 2);
    }

    if (!!objectPosition) {
      this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectPosition', `${objectPosition}`, 2);
    }
  }

  public getPortraitImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.portraitImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      impolicy: this.settings.general.impolicy
    };

    if (this.hasToApplyAkamaiOptimization) {
      akamaiOptions.width = this.applyAkamaiOptimization(this.settings.general.portraitImage);
    } else {
      akamaiOptions.width = this.assetsSharedService.getWidth(this.elRef);
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      height: toNumber(this.settings.general.portraitImage?.height),
      width: toNumber(this.settings.general.portraitImage?.width),
    };
  }

  public getLandscapeImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = this.settings.general.landscapeImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      impolicy: this.settings.general.impolicy
    };

    if (this.hasToApplyAkamaiOptimization) {
      akamaiOptions.width = this.applyAkamaiOptimization(this.settings.general.landscapeImage);
    } else {
      akamaiOptions.width = this.assetsSharedService.getWidth(this.elRef);
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      width: toNumber(this.settings.general.landscapeImage?.width),
      height: toNumber(this.settings.general.landscapeImage?.height),
    };
  }

  public setAnalyticsEvents() {
    const eventClickImageInfo = this.settings.analytics?.events.find((ev: any) => ev.name === 'click-image');
    this.analyticsEventClickImage = eventClickImageInfo?.relatedEvent;

    this.analyticsEventData = {
      extraInfo: eventClickImageInfo?.extraInfo,
      categoryId: this.settings.general?.link?.categoryId,
      src: this.settings.general?.link?.src,
      pieceName: this.settings.general?.link?.pieceName
    };
  }

  public onRenderLinkClick(productInfo: { productId: string | undefined; categoryId: string | undefined }): void {
    if (productInfo.productId) {
      this.productService.productDetail(productInfo.productId, productInfo.categoryId).pipe(
        take(1)
      ).subscribe((product) => {
        const cmsEvent: CMSEvent<Product> = {
          name: 'productLink',
          type: 'click',
          data: product
        };
        this.cmsRenderEventsService.emitEvent(cmsEvent);
      });
    }
  }

  private applyAkamaiOptimization(image: IdtImage | null | undefined): number {
    return this.assetsSharedService.getOptimumImageWidth(
      toNumber(image?.height)!,
      toNumber(image?.width)!,
      this.window.innerHeight,
      this.assetsSharedService.getWidth(this.elRef)
    );
  }

}
